/**
 * Page-named settings — every operational screen can have:
 * 1) Related central settings (Control Center deep-links)
 * 2) Page-only controls stored as SystemSetting key `Page.{pageId}` (JSON bag)
 *
 * Match uses the same path rules as Smart Guide (exact, :param, prefix*).
 */

import type { SettingsDomainId, SettingsScreenContext } from './settingsRegistry'

export type PageSettingValueType = 'boolean' | 'number' | 'string'

export interface PageSettingLabel {
  ar: string
  en: string
  fr?: string
}

export interface PageSettingField {
  /** Key inside the Page.{pageId} JSON bag */
  key: string
  label: PageSettingLabel
  description?: PageSettingLabel
  type: PageSettingValueType
  defaultValue: boolean | number | string
  /** Optional min/max for numbers */
  min?: number
  max?: number
  /** Group heading inside the panel */
  section?: PageSettingLabel
}

export interface PageSettingsDef {
  /** Stable id used in `Page.{pageId}` — usually PascalCase page name */
  pageId: string
  label: PageSettingLabel
  /** Path patterns: `/invoices`, `/invoices/:id/edit`, `/reports/*` */
  match: string | string[]
  domain: SettingsDomainId
  context: SettingsScreenContext
  /** Existing Settings Control Center item ids to surface as “related” */
  relatedItemIds?: string[]
  /** Controls that only affect this page */
  fields?: PageSettingField[]
}

/** Shared defaults applied to every list/form page unless overridden */
export const COMMON_LIST_FIELDS: PageSettingField[] = [
  {
    key: 'pageSize',
    label: { ar: 'عدد الصفوف في الصفحة', en: 'Rows per page', fr: 'Lignes par page' },
    type: 'number',
    defaultValue: 25,
    min: 5,
    max: 200,
    section: { ar: 'العرض', en: 'Display', fr: 'Affichage' },
  },
  {
    key: 'compactMode',
    label: { ar: 'وضع مضغوط', en: 'Compact mode', fr: 'Mode compact' },
    description: { ar: 'تقليل المسافات في الجداول والنماذج', en: 'Tighter spacing in grids and forms', fr: 'Espacement plus serré dans les grilles et formulaires' },
    type: 'boolean',
    defaultValue: false,
    section: { ar: 'العرض', en: 'Display', fr: 'Affichage' },
  },
  {
    key: 'rememberFilters',
    label: { ar: 'تذكّر عوامل التصفية', en: 'Remember filters', fr: 'Mémoriser les filtres' },
    type: 'boolean',
    defaultValue: true,
    section: { ar: 'التصفية', en: 'Filters', fr: 'Filtres' },
  },
  {
    key: 'showInactive',
    label: { ar: 'إظهار غير النشط', en: 'Show inactive', fr: 'Afficher les inactifs' },
    type: 'boolean',
    defaultValue: false,
    section: { ar: 'التصفية', en: 'Filters', fr: 'Filtres' },
  },
  {
    key: 'autoRefreshSeconds',
    label: { ar: 'تحديث تلقائي (ثانية، 0 = إيقاف)', en: 'Auto-refresh (seconds, 0 = off)', fr: 'Actualisation auto (secondes, 0 = off)' },
    type: 'number',
    defaultValue: 0,
    min: 0,
    max: 600,
    section: { ar: 'التحديث', en: 'Refresh', fr: 'Actualisation' },
  },
  {
    key: 'confirmBeforeDelete',
    label: { ar: 'تأكيد قبل الحذف', en: 'Confirm before delete', fr: 'Confirmer avant suppression' },
    type: 'boolean',
    defaultValue: true,
    section: { ar: 'الأمان', en: 'Safety', fr: 'Sécurité' },
  },
]

export const COMMON_FORM_FIELDS: PageSettingField[] = [
  {
    key: 'requireNotes',
    label: { ar: 'ملاحظات إلزامية', en: 'Require notes', fr: 'Notes obligatoires' },
    type: 'boolean',
    defaultValue: false,
    section: { ar: 'الحقول', en: 'Fields', fr: 'Champs' },
  },
  {
    key: 'autoFocusFirstField',
    label: { ar: 'التركيز على أول حقل', en: 'Auto-focus first field', fr: 'Focus sur le premier champ' },
    type: 'boolean',
    defaultValue: true,
    section: { ar: 'الإدخال', en: 'Input', fr: 'Saisie' },
  },
  {
    key: 'warnOnUnsavedLeave',
    label: { ar: 'تحذير عند مغادرة التعديلات غير المحفوظة', en: 'Warn on unsaved leave', fr: 'Avertir en cas de modifications non enregistrées' },
    type: 'boolean',
    defaultValue: true,
    section: { ar: 'الأمان', en: 'Safety', fr: 'Sécurité' },
  },
]

function listPage(
  pageId: string,
  label: PageSettingLabel,
  match: string | string[],
  domain: SettingsDomainId,
  context: SettingsScreenContext,
  relatedItemIds: string[] = [],
  extraFields: PageSettingField[] = []
): PageSettingsDef {
  return {
    pageId,
    label,
    match,
    domain,
    context,
    relatedItemIds,
    fields: [...COMMON_LIST_FIELDS, ...extraFields],
  }
}

function formPage(
  pageId: string,
  label: PageSettingLabel,
  match: string | string[],
  domain: SettingsDomainId,
  context: SettingsScreenContext,
  relatedItemIds: string[] = [],
  extraFields: PageSettingField[] = []
): PageSettingsDef {
  return {
    pageId,
    label,
    match,
    domain,
    context,
    relatedItemIds,
    fields: [...COMMON_FORM_FIELDS, ...extraFields],
  }
}

/**
 * Explicit page catalog. Unlisted routes still get a fallback via resolvePageSettings().
 */
export const PAGE_SETTINGS_REGISTRY: PageSettingsDef[] = [
  // —— Core ——
  listPage('Dashboard', { ar: 'لوحة المعلومات', en: 'Dashboard' }, '/dashboard', 'system', 'system', [
    'sys-companies',
    'sys-colors',
    'sys-sounds',
  ]),
  listPage('Companies', { ar: 'الشركات', en: 'Companies' }, ['/companies', '/companies/*'], 'system', 'system', [
    'sys-companies',
    'sys-company-settings',
  ]),
  listPage('Branches', { ar: 'الفروع', en: 'Branches' }, ['/branches', '/branches/*'], 'system', 'system', [
    'sys-branches',
  ]),
  listPage('Stores', { ar: 'المخازن', en: 'Stores' }, ['/stores', '/stores/*'], 'inventory', 'inventory', [
    'inv-stores',
  ]),
  listPage('FiscalYears', { ar: 'السنوات المالية', en: 'Fiscal years', fr: 'Exercices fiscaux' }, ['/fiscal-years', '/fiscal-years/*'], 'accounting', 'accounting', [
    'acc-fiscal-years',
  ]),

  // —— Catalog ——
  listPage(
    'Items',
    { ar: 'الأصناف', en: 'Items', fr: 'Articles' },
    ['/items', '/items-directory'],
    'inventory',
    'inventory',
    ['inv-barcode', 'inv-units', 'inv-categories', 'sys-dropdown-fields'],
    [
      {
        key: 'defaultStockOnly',
        label: { ar: 'عرض المتوفر فقط افتراضياً', en: 'Default stock-only filter', fr: 'Filtre stock uniquement par défaut' },
        type: 'boolean',
        defaultValue: false,
        section: { ar: 'المخزون', en: 'Stock', fr: 'Stock' },
      },
    ]
  ),
  formPage(
    'NewItem',
    { ar: 'صنف جديد / تعديل', en: 'New / edit item', fr: 'Nouvel article / modification' },
    ['/items/new', '/items/:id/edit', '/items/:id/view', '/items/:id/copy'],
    'inventory',
    'inventory',
    ['inv-barcode', 'inv-units', 'sys-dropdown-fields'],
    [
      {
        key: 'requireBarcode',
        label: { ar: 'الباركود إلزامي', en: 'Require barcode', fr: 'Code-barres obligatoire' },
        type: 'boolean',
        defaultValue: false,
        section: { ar: 'الحقول', en: 'Fields', fr: 'Champs' },
      },
      {
        key: 'requireCategory',
        label: { ar: 'الفئة إلزامية', en: 'Require category', fr: 'Catégorie obligatoire' },
        type: 'boolean',
        defaultValue: false,
        section: { ar: 'الحقول', en: 'Fields', fr: 'Champs' },
      },
    ]
  ),
  listPage('Categories', { ar: 'الفئات', en: 'Categories', fr: 'Catégories' }, ['/categories', '/categories/*'], 'inventory', 'inventory', [
    'inv-categories',
  ]),
  listPage('Modifiers', { ar: 'المعدّلات', en: 'Modifiers', fr: 'Modificateurs' }, ['/modifiers', '/modifiers/*'], 'inventory', 'inventory'),
  listPage('Medicines', { ar: 'الأدوية', en: 'Medicines', fr: 'Médicaments' }, ['/medicines', '/medicines/*'], 'inventory', 'inventory', [
    'sys-medicine',
  ]),
  listPage('BarcodePrint', { ar: 'طباعة باركود', en: 'Barcode print', fr: 'Impression de codes-barres' }, '/barcode-print', 'printing', 'printing', [
    'inv-barcode',
    'reports-print',
  ]),

  // —— Partners ——
  listPage(
    'Customers',
    { ar: 'العملاء', en: 'Customers', fr: 'Clients' },
    ['/customers', '/customers/*', '/customer-categories'],
    'customers',
    'customers',
    ['partners-customers', 'partners-restrict', 'sys-dropdown-fields'],
    [
      {
        key: 'requirePhone',
        label: { ar: 'الهاتف إلزامي', en: 'Require phone', fr: 'Téléphone obligatoire' },
        type: 'boolean',
        defaultValue: false,
        section: { ar: 'الحقول', en: 'Fields', fr: 'Champs' },
      },
      {
        key: 'requireTaxNumber',
        label: { ar: 'الرقم الضريبي إلزامي', en: 'Require tax number', fr: 'Numéro fiscal obligatoire' },
        type: 'boolean',
        defaultValue: false,
        section: { ar: 'الحقول', en: 'Fields', fr: 'Champs' },
      },
    ]
  ),
  listPage('Vendors', { ar: 'الموردون', en: 'Vendors', fr: 'Fournisseurs' }, ['/vendors', '/vendors/*'], 'suppliers', 'suppliers', [
    'partners-vendors',
    'pur-vendors',
    'sys-dropdown-fields',
  ]),
  listPage('Contacts', { ar: 'جهات الاتصال', en: 'Contacts', fr: 'Contacts' }, ['/contacts', '/contacts/*'], 'customers', 'customers'),
  listPage('SalesReps', { ar: 'مندوبو المبيعات', en: 'Sales reps', fr: 'Représentants' }, ['/sales-reps', '/sales-reps/*'], 'sales', 'sales'),

  // —— Inventory ops ——
  listPage('Inventory', { ar: 'المخزون', en: 'Inventory', fr: 'Inventaire' }, '/inventory', 'inventory', 'inventory', [
    'inv-valuation',
    'inv-stock-take',
    'inv-stores',
  ]),
  listPage(
    'InventoryAdjustments',
    { ar: 'تسويات المخزون', en: 'Inventory adjustments', fr: 'Ajustements d\'inventaire' },
    ['/inventory-adjustments', '/inventory-adjustments/*'],
    'inventory',
    'inventory',
    ['inv-stock-take']
  ),
  listPage(
    'BranchTransfers',
    { ar: 'تحويلات الفروع', en: 'Branch transfers', fr: 'Transferts entre succursales' },
    ['/branch-transfers', '/branch-transfers/*'],
    'inventory',
    'inventory',
    ['inv-transfer']
  ),
  listPage(
    'OpeningStock',
    { ar: 'أرصدة افتتاحية', en: 'Opening stock', fr: 'Stock d\'ouverture' },
    ['/opening-stock-invoices', '/opening-stock-invoices/*'],
    'inventory',
    'inventory',
    ['inv-opening']
  ),
  listPage('WarehouseBins', { ar: 'مواقع التخزين', en: 'Warehouse bins', fr: 'Emplacements de stockage' }, ['/warehouse-bins', '/warehouse-bins/*'], 'inventory', 'inventory', [
    'inv-bins',
  ]),

  // —— Sales ——
  listPage(
    'Invoices',
    { ar: 'فواتير المبيعات', en: 'Sales invoices', fr: 'Factures de vente' },
    '/invoices',
    'sales',
    'sales',
    ['sales-invoice-settings', 'sales-payment', 'acc-tax-included', 'reports-print'],
    [
      {
        key: 'defaultPaid',
        label: { ar: 'افتراض فاتورة مدفوعة', en: 'Default as paid', fr: 'Payée par défaut' },
        type: 'boolean',
        defaultValue: false,
        section: { ar: 'الافتراضات', en: 'Defaults', fr: 'Par défaut' },
      },
      {
        key: 'hideZeroStockItems',
        label: { ar: 'إخفاء الأصناف بدون رصيد', en: 'Hide zero-stock items', fr: 'Masquer les articles sans stock' },
        type: 'boolean',
        defaultValue: false,
        section: { ar: 'الأصناف', en: 'Items', fr: 'Articles' },
      },
    ]
  ),
  formPage(
    'NewInvoice',
    { ar: 'فاتورة مبيعات', en: 'Sales invoice form', fr: 'Formulaire de facture de vente' },
    ['/invoices/new', '/invoices/:id/edit', '/invoices/:id/view', '/invoices/:id/copy'],
    'sales',
    'sales',
    ['sales-invoice-settings', 'acc-tax-included', 'reports-print'],
    [
      {
        key: 'requireCustomer',
        label: { ar: 'العميل إلزامي', en: 'Require customer', fr: 'Client obligatoire' },
        type: 'boolean',
        defaultValue: true,
        section: { ar: 'الحقول', en: 'Fields', fr: 'Champs' },
      },
      {
        key: 'requireSalesRep',
        label: { ar: 'المندوب إلزامي', en: 'Require sales rep', fr: 'Représentant obligatoire' },
        type: 'boolean',
        defaultValue: false,
        section: { ar: 'الحقول', en: 'Fields', fr: 'Champs' },
      },
      {
        key: 'allowPriceEdit',
        label: { ar: 'السماح بتعديل السعر', en: 'Allow price edit', fr: 'Autoriser la modification du prix' },
        type: 'boolean',
        defaultValue: true,
        section: { ar: 'الأسعار', en: 'Pricing', fr: 'Tarification' },
      },
      {
        key: 'allowDiscount',
        label: { ar: 'السماح بالخصم', en: 'Allow discount', fr: 'Autoriser la remise' },
        type: 'boolean',
        defaultValue: true,
        section: { ar: 'الأسعار', en: 'Pricing', fr: 'Tarification' },
      },
      {
        key: 'autoPrintAfterSave',
        label: { ar: 'طباعة تلقائية بعد الحفظ', en: 'Auto-print after save', fr: 'Impression automatique après l\'enregistrement' },
        type: 'boolean',
        defaultValue: false,
        section: { ar: 'الطباعة', en: 'Print', fr: 'Impression' },
      },
    ]
  ),
  listPage('SalesReturns', { ar: 'مرتجعات المبيعات', en: 'Sales returns', fr: 'Retours de ventes' }, ['/sales-returns', '/sales-returns/*'], 'sales', 'sales', [
    'sales-invoice-settings',
  ]),
  listPage('SalesOrders', { ar: 'أوامر البيع', en: 'Sales orders', fr: 'Commandes de vente' }, ['/sales-orders', '/sales-orders/*'], 'sales', 'sales'),
  listPage('Quotations', { ar: 'عروض الأسعار', en: 'Quotations', fr: 'Devis' }, ['/quotations', '/quotations/*'], 'sales', 'sales'),
  listPage('InvoiceDesigns', { ar: 'تصاميم الفواتير', en: 'Invoice designs', fr: 'Modèles de factures' }, ['/invoices/designs', '/invoices/designs/*'], 'printing', 'printing', [
    'reports-print',
    'sales-pos',
  ]),

  // —— Purchases ——
  listPage(
    'PurchaseInvoices',
    { ar: 'فواتير المشتريات', en: 'Purchase invoices', fr: 'Factures d\'achat' },
    '/purchase-invoices',
    'purchases',
    'purchases',
    ['pur-orders', 'pur-grn', 'acc-payment-methods']
  ),
  listPage(
    'LandedCosts',
    { ar: 'تكاليف إضافية على المشتريات', en: 'Landed costs', fr: 'Coûts additionnels' },
    ['/landed-costs', '/landed-costs/*'],
    'purchases',
    'purchases'
  ),
  formPage(
    'NewPurchaseInvoice',
    { ar: 'فاتورة مشتريات', en: 'Purchase invoice form', fr: 'Formulaire de facture d\'achat' },
    ['/purchase-invoices/new', '/purchase-invoices/:id/edit', '/purchase-invoices/:id/view', '/purchase-invoices/:id/copy'],
    'purchases',
    'purchases',
    ['pur-orders', 'pur-grn'],
    [
      {
        key: 'requireVendor',
        label: { ar: 'المورد إلزامي', en: 'Require vendor', fr: 'Fournisseur obligatoire' },
        type: 'boolean',
        defaultValue: true,
        section: { ar: 'الحقول', en: 'Fields', fr: 'Champs' },
      },
    ]
  ),
  listPage(
    'PurchaseReturns',
    { ar: 'مرتجعات المشتريات', en: 'Purchase returns', fr: 'Retours d\'achat' },
    ['/purchase-returns', '/purchase-returns/*'],
    'purchases',
    'purchases'
  ),
  listPage('PurchaseOrders', { ar: 'طلبية الشراء', en: 'Purchase orders', fr: 'Commandes d\'achat' }, ['/purchase-orders', '/purchase-orders/*'], 'purchases', 'purchases', [
    'pur-orders',
  ]),
  listPage('Tenders', { ar: 'المناقصات', en: 'Tenders', fr: 'Appels d’offres' }, ['/tenders', '/tenders/*'], 'purchases', 'purchases'),

  // —— POS / Restaurant ——
  listPage('POS', { ar: 'نقطة البيع', en: 'POS', fr: 'Caisse' }, '/pos', 'pos', 'pos', [
    'sales-invoice-settings',
    'sales-pos-display',
    'sales-pos',
    'reports-print',
  ]),
  listPage('Kitchen', { ar: 'شاشة المطبخ', en: 'Kitchen display', fr: 'Écran cuisine' }, '/kitchen', 'pos', 'pos', ['sales-pos']),
  listPage('DeliveryOrders', { ar: 'طلبات التوصيل', en: 'Delivery orders', fr: 'Commandes de livraison' }, '/restaurant/delivery', 'pos', 'pos', ['sales-pos']),
  listPage('OrdersQueue', { ar: 'طابور الطلبات', en: 'Orders queue', fr: 'File des commandes' }, '/restaurant/orders-queue', 'pos', 'pos', ['sales-pos']),
  listPage('MenuSetup', { ar: 'إعداد القائمة', en: 'Menu setup', fr: 'Configuration du menu' }, '/restaurant/menu-setup', 'pos', 'pos'),
  listPage('Shifts', { ar: 'الورديات', en: 'Shifts', fr: 'Services' }, ['/shifts', '/shifts/*'], 'pos', 'pos'),
  listPage('Tables', { ar: 'الطاولات', en: 'Tables', fr: 'Tables' }, ['/tables', '/tables/*'], 'pos', 'pos'),

  // —— Accounting ——
  listPage('Journal', { ar: 'القيود اليومية', en: 'Journal entries', fr: 'Écritures de journal' }, '/journal', 'accounting', 'accounting', [
    'acc-prevent-journal-after-first-movement',
    'acc-journal-gen',
    'acc-post-on-save',
    'sys-doc-numbering',
  ]),
  formPage(
    'NewJournal',
    { ar: 'قيد يومية', en: 'Journal entry form', fr: 'Formulaire d\'écriture' },
    ['/journal/new', '/journal/:id/edit', '/journal/:id/view'],
    'accounting',
    'accounting',
    ['acc-prevent-journal-after-first-movement', 'acc-journal-gen', 'acc-post-on-save'],
    [
      {
        key: 'requireCostCenter',
        label: { ar: 'مركز التكلفة إلزامي', en: 'Require cost center', fr: 'Centre de coût obligatoire' },
        type: 'boolean',
        defaultValue: false,
        section: { ar: 'الحقول', en: 'Fields', fr: 'Champs' },
      },
      {
        key: 'defaultOperationType',
        label: { ar: 'نوع القيد الافتراضي', en: 'Default entry type', fr: 'Type d\'écriture par défaut' },
        description: {
          ar: 'يُختار تلقائيًا عند إنشاء قيد جديد. القيمة المبرمجة: قيد يومية',
          en: 'Selected automatically on a new journal. Programmed default: Journal Entry',
          fr: 'Sélectionné automatiquement. Valeur programmée : Journal Entry',
        },
        type: 'string',
        defaultValue: 'قيد يومية',
        section: { ar: 'القيم الافتراضية', en: 'Defaults', fr: 'Valeurs par défaut' },
      },
    ]
  ),
  listPage('AllJournalEntries', { ar: 'كل القيود', en: 'All journal entries', fr: 'Toutes les écritures' }, '/all-journal-entries', 'accounting', 'accounting', [
    'acc-journal-gen',
  ]),
  listPage('Vouchers', { ar: 'سندات القبض والصرف', en: 'Vouchers', fr: 'Pièces de caisse' }, ['/vouchers', '/vouchers/*'], 'accounting', 'accounting', [
    'acc-payment-methods',
  ]),
  listPage('ChartOfAccounts', { ar: 'دليل الحسابات', en: 'Chart of accounts', fr: 'Plan comptable' }, '/chart-of-accounts', 'accounting', 'accounting', [
    'acc-coa',
  ]),
  listPage('CostCenters', { ar: 'مراكز التكلفة', en: 'Cost centers', fr: 'Centres de coût' }, ['/cost-centers', '/cost-centers/*'], 'accounting', 'accounting', [
    'acc-cost-centers',
  ]),
  listPage(
    'BankReconciliation',
    { ar: 'التسوية البنكية', en: 'Bank reconciliation', fr: 'Rapprochement bancaire' },
    ['/bank-reconciliation', '/bank-reconciliation/*'],
    'accounting',
    'accounting'
  ),
  listPage('Taxes', { ar: 'الضرائب', en: 'Taxes', fr: 'Taxes' }, ['/taxes', '/taxes/*'], 'tax', 'tax', [
    'acc-tax-included',
    'acc-e-invoice',
  ]),

  // —— Reports (prefix catch-all) ——
  listPage('Reports', { ar: 'التقارير', en: 'Reports', fr: 'Rapports' }, ['/reports', '/reports/*'], 'reports', 'reports', [
    'reports-export',
    'reports-print',
    'sys-export',
  ]),

  // —— Production ——
  listPage('BOMs', { ar: 'قوائم المواد', en: 'BOMs', fr: 'Nomenclatures' }, ['/production/boms', '/production/boms/*'], 'production', 'production', [
    'prd-boms',
  ]),
  listPage(
    'ProductionOrders',
    { ar: 'أوامر الإنتاج', en: 'Production orders', fr: 'Ordres de production' },
    ['/production/orders', '/production/orders/*'],
    'production',
    'production',
    ['prd-orders']
  ),
  listPage('Sorting', { ar: 'الفرز', en: 'Sorting', fr: 'Triage' }, ['/sorting', '/sorting/*'], 'production', 'production'),

  // —— Assets / Budget ——
  listPage('FixedAssets', { ar: 'الأصول الثابتة', en: 'Fixed assets', fr: 'Immobilisations' }, ['/fixed-assets', '/fixed-assets/*'], 'accounting', 'accounting'),
  listPage('Budgets', { ar: 'الموازنات', en: 'Budgets', fr: 'Budgets' }, ['/budgets', '/budgets/*'], 'accounting', 'accounting'),

  // —— HR ——
  listPage('HR', { ar: 'الموارد البشرية', en: 'HR', fr: 'RH' }, ['/hr', '/hr/*'], 'hr', 'hr', [
    'hr-employees',
    'hr-attendance',
    'hr-payroll',
  ]),

  // —— Dental ——
  listPage('Patients', { ar: 'المرضى', en: 'Patients', fr: 'Patients' }, ['/patients', '/patients/*'], 'customers', 'customers'),
  listPage('Appointments', { ar: 'المواعيد', en: 'Appointments', fr: 'Rendez-vous' }, ['/appointments', '/appointments/*'], 'system', 'system'),
  listPage('DentalServices', { ar: 'خدمات الأسنان', en: 'Dental services', fr: 'Services dentaires' }, ['/dental/services', '/dental/services/*'], 'sales', 'sales'),
  listPage('DentalItems', { ar: 'أصناف الأسنان', en: 'Dental items', fr: 'Articles dentaires' }, ['/dental/items', '/dental/items/*'], 'inventory', 'inventory'),
  listPage('DentalDashboard', { ar: 'لوحة الأسنان', en: 'Dental dashboard', fr: 'Tableau de bord dentaire' }, '/dental/dashboard', 'system', 'system'),

  // —— Security ops ——
  listPage('Users', { ar: 'المستخدمون', en: 'Users', fr: 'Utilisateurs' }, ['/users', '/users/*'], 'security', 'security', ['usr-users', 'usr-roles']),
  listPage('Permissions', { ar: 'الصلاحيات', en: 'Permissions', fr: 'Autorisations' }, ['/permissions', '/permissions/*'], 'security', 'security', [
    'usr-permissions',
    'usr-action-perms',
  ]),
  listPage('Roles', { ar: 'الأدوار', en: 'Roles', fr: 'Rôles' }, ['/roles', '/roles/*'], 'security', 'security', ['usr-roles']),

  // —— Devices / sync ——
  listPage('Signals', { ar: 'الإشارات', en: 'Signals', fr: 'Signaux' }, ['/signals', '/signals/*'], 'devices', 'devices', ['sys-signals']),
  listPage('DeviceSync', { ar: 'مزامنة الأجهزة', en: 'Device sync', fr: 'Synchronisation des appareils' }, ['/device-sync', '/device-sync/*'], 'devices', 'devices', [
    'sys-device-sync',
  ]),
  listPage('ConnectedDevices', { ar: 'الأجهزة المتصلة', en: 'Connected devices', fr: 'Appareils connectés' }, ['/connected-devices', '/connected-devices/*'], 'devices', 'devices', [
    'sys-connected-devices',
  ]),
  listPage('TailscaleDevices', { ar: 'أجهزة الشبكة', en: 'Tailscale devices', fr: 'Appareils Tailscale' }, ['/tailscale-devices', '/tailscale-devices/*'], 'devices', 'devices', [
    'sys-tailscale-devices',
  ]),
  listPage('ESL', { ar: 'الملصقات الإلكترونية', en: 'ESL', fr: 'Étiquettes électroniques' }, ['/esl', '/esl/*'], 'devices', 'devices'),

  // —— Maintenance / car rental / municipality (prefix) ——
  listPage('Maintenance', { ar: 'الصيانة', en: 'Maintenance', fr: 'Maintenance' }, ['/maintenance', '/maintenance/*'], 'system', 'system'),
  listPage('CarRental', { ar: 'تأجير السيارات', en: 'Car rental', fr: 'Location de voitures' }, ['/car-rental', '/car-rental/*'], 'sales', 'sales'),
  listPage('FuelStation', { ar: 'محطات المحروقات', en: 'Fuel stations', fr: 'Stations-service' }, ['/fuel-station', '/fuel-station/*', '/fuel-meter'], 'inventory', 'inventory'),
  listPage('Municipality', { ar: 'البلدية', en: 'Municipality', fr: 'Municipalité' }, ['/municipality', '/municipality/*'], 'system', 'system'),
]

/** Prefix → domain/context for routes not in the explicit registry */
export const PREFIX_DOMAIN: Array<{ prefix: string; domain: SettingsDomainId; context: SettingsScreenContext }> = [
  { prefix: '/invoices', domain: 'sales', context: 'sales' },
  { prefix: '/sales-', domain: 'sales', context: 'sales' },
  { prefix: '/quotations', domain: 'sales', context: 'sales' },
  { prefix: '/pos', domain: 'pos', context: 'pos' },
  { prefix: '/restaurant', domain: 'pos', context: 'pos' },
  { prefix: '/kitchen', domain: 'pos', context: 'pos' },
  { prefix: '/purchase', domain: 'purchases', context: 'purchases' },
  { prefix: '/tenders', domain: 'purchases', context: 'purchases' },
  { prefix: '/inventory', domain: 'inventory', context: 'inventory' },
  { prefix: '/items', domain: 'inventory', context: 'inventory' },
  { prefix: '/fuel-station', domain: 'inventory', context: 'inventory' },
  { prefix: '/stores', domain: 'inventory', context: 'inventory' },
  { prefix: '/categories', domain: 'inventory', context: 'inventory' },
  { prefix: '/warehouse', domain: 'inventory', context: 'inventory' },
  { prefix: '/branch-transfers', domain: 'inventory', context: 'inventory' },
  { prefix: '/opening-stock', domain: 'inventory', context: 'inventory' },
  { prefix: '/customers', domain: 'customers', context: 'customers' },
  { prefix: '/vendors', domain: 'suppliers', context: 'suppliers' },
  { prefix: '/contacts', domain: 'customers', context: 'customers' },
  { prefix: '/journal', domain: 'accounting', context: 'accounting' },
  { prefix: '/vouchers', domain: 'accounting', context: 'accounting' },
  { prefix: '/chart-of-accounts', domain: 'accounting', context: 'accounting' },
  { prefix: '/cost-centers', domain: 'accounting', context: 'accounting' },
  { prefix: '/bank-reconciliation', domain: 'accounting', context: 'accounting' },
  { prefix: '/fiscal-years', domain: 'accounting', context: 'accounting' },
  { prefix: '/taxes', domain: 'tax', context: 'tax' },
  { prefix: '/reports', domain: 'reports', context: 'reports' },
  { prefix: '/hr', domain: 'hr', context: 'hr' },
  { prefix: '/production', domain: 'production', context: 'production' },
  { prefix: '/users', domain: 'security', context: 'security' },
  { prefix: '/permissions', domain: 'security', context: 'security' },
  { prefix: '/roles', domain: 'security', context: 'security' },
  { prefix: '/settings', domain: 'system', context: 'system' },
  { prefix: '/signals', domain: 'devices', context: 'devices' },
  { prefix: '/device-sync', domain: 'devices', context: 'devices' },
  { prefix: '/connected-devices', domain: 'devices', context: 'devices' },
  { prefix: '/tailscale-devices', domain: 'devices', context: 'devices' },
  { prefix: '/esl', domain: 'devices', context: 'devices' },
  { prefix: '/companies', domain: 'system', context: 'system' },
  { prefix: '/branches', domain: 'system', context: 'system' },
]

export function pageSettingKey(pageId: string): string {
  return `Page.${pageId}`
}

export function getPageDefById(pageId: string): PageSettingsDef | undefined {
  return PAGE_SETTINGS_REGISTRY.find((p) => p.pageId === pageId)
}

export function getAllPageDefs(): PageSettingsDef[] {
  return PAGE_SETTINGS_REGISTRY
}

export function defaultsForPage(def: PageSettingsDef): Record<string, boolean | number | string> {
  const out: Record<string, boolean | number | string> = {}
  for (const f of def.fields || []) {
    out[f.key] = f.defaultValue
  }
  return out
}
